import type { AsyncDataExecuteOptions } from '#app/composables/asyncData'
import type { CreateAddressInput, CreateCustomerInput, ErrorResultFragment, InputMaybe, UpdateOrderCustomFieldsInput as IUpdateOrderCustomFieldsInput, UpdateOrderInput as IUpdateOrderInput, OrderFragment, OrderLineFragment, PaymentInput, PaymentMethodQuoteFragment, ProductVariantFragment, ShippingMethodQuoteFragment } from '#graphql-operations'
import type { ComputedRef, Ref } from 'vue'
import type { PaymentMethod } from '~/composables/useCheckout'
import type { CustomerEmailSyncOptions } from '~/utils/prizeEmailGuard'
import { ErrorCode } from '#graphql-operations'
import destr from 'destr'
import { AlreadyLoggedInError, CouponCodeCombinationError, CouponCodeExpiredError, CouponCodeInvalidError, CouponCodeLimitError, CouponRemovedDuringCheckoutError, EmailAddressConflictError, GuestCheckoutError, IneligiblePaymentMethodError, IneligibleShippingMethodError, InsufficientStockError, NegativeQuantityError, NoActiveOrderError, OrderLimitError, OrderModificationError, OrderPaymentStateError, OrderStateTransitionError, PaymentDeclinedError, PaymentFailedError } from '~/constants/errors'
import { createPrizeEmailGuard } from '~/utils/prizeEmailGuard'

/** @see https://github.com/exaltedstore/exalted-backend/blob/develop/src/utils/order.ts */
export interface ParcelPoint {
  code: string
  name: string
  street: string
  city: string
  zipCode: string
  country: string
  lat: string | number
  lng: string | number
  //
  courier: string // custom, should match balikobot
}

export interface UpdateOrderCustomFieldsInput extends Omit<IUpdateOrderCustomFieldsInput, 'parcelPoint'> {
  parcelPoint?: InputMaybe<ParcelPoint>
}

export interface UpdateOrderInput extends Omit<IUpdateOrderInput, 'customFields'> {
  customFields?: InputMaybe<UpdateOrderCustomFieldsInput>
}

interface CustomOrderStates {}
export type OrderState = | 'Created' | 'Draft' | 'AddingItems' | 'ArrangingPayment' | 'Validated' | 'PaymentAuthorized' | 'PaymentSettled' | 'PartiallyShipped' | 'Shipped' | 'PartiallyDelivered' | 'Delivered' | 'Modifying' | 'ArrangingAdditionalPayment' | 'Cancelled' | keyof CustomOrderStates

export default defineNuxtPlugin(async (nuxtApp) => {
  if (nuxtApp.payload.error)
    return {}

  // const { $localePath } = useNuxtApp()
  const activeOrderError = useState<ErrorResultFragment>('activeOrderError')
  const { data: activeOrder, refresh: refreshActiveOrder } = await useAsyncData('activeOrder', async () => {
    const { data } = await useGraphqlQuery('activeOrder')
    return data.activeOrder
  }, { lazy: true, server: false })

  const hasActiveOrder = computed(() => activeOrder.value && activeOrder.value.active && activeOrder.value.lines.length > 0)
  const cartDrawerOpen = useState<boolean>('cartDrawerOpen')

  // addRouteMiddleware('checkout', (to, from) => {
  //   // if its a stripe redirect, do not redirect away
  //   if (to.query.payment_intent && to.query.payment_intent_client_secret && to.query.redirect_status)
  //     return
  //
  //   if (to.meta.checkout && !hasActiveOrder.value) {
  //     if (import.meta.server && to.path === from.path)
  //       return navigateTo($localePath('/'))
  //     return navigateTo($localePath('/'))
  //   }
  // }, { global: true })
  //
  // const currentRoute = useRoute()
  //
  // if (import.meta.client) {
  //   watch(hasActiveOrder, async (hasActiveOrder) => {
  //     if (currentRoute.meta.checkout && !hasActiveOrder)
  //       await navigateTo($localePath('/'))
  //   })
  // }

  // Order State
  const transitionOrderToState = async (nextState: OrderState) => {
    if (activeOrder.value?.state === nextState || !hasActiveOrder.value)
      return

    const { data } = await useGraphqlQuery('nextOrderStates')

    if (data.nextOrderStates.includes(nextState)) {
      const { data } = await useGraphqlMutation('transitionOrderToState', { state: nextState })

      if (isGraphqlError(data.transitionOrderToState)) {
        activeOrderError.value = data.transitionOrderToState
        // Surfacing the failure is the caller's job: both checkout flows catch
        // the OrderStateTransitionError below and translate its `transitionError`
        // (the real reason) — toasting here too would double up and, for
        // 'Validated', show the cryptic generic message instead.
        switch (data.transitionOrderToState.errorCode) {
          case ErrorCode.ORDER_STATE_TRANSITION_ERROR: {
            throw new OrderStateTransitionError(data.transitionOrderToState.message, data.transitionOrderToState.fromState, data.transitionOrderToState.toState, data.transitionOrderToState.transitionError)
          }
          default: {
            throw new Error(data.transitionOrderToState.message)
          }
        }
      }

      if (isGraphqlType(data.transitionOrderToState, 'Order')) {
        activeOrder.value = data.transitionOrderToState
        return data.transitionOrderToState
      }

      throw new Error('Failed to transition order to state')
    }

    throw new Error('Invalid order state transition')
  }

  // Cart
  const addItemToOrder = async (productVariant: ProductVariantFragment, quantity = 1) => {
    await transitionOrderToState('AddingItems')
    const { data } = await useGraphqlMutation('addItemToOrder', { productVariantId: productVariant.id, quantity: +quantity })

    if (isGraphqlError(data.addItemToOrder)) {
      activeOrderError.value = data.addItemToOrder
      switch (data.addItemToOrder.errorCode) {
        case ErrorCode.INSUFFICIENT_STOCK_ERROR: {
          throw new InsufficientStockError(data.addItemToOrder.message, data.addItemToOrder.quantityAvailable, data.addItemToOrder.order)
        }
        case ErrorCode.NEGATIVE_QUANTITY_ERROR: {
          throw new NegativeQuantityError(data.addItemToOrder.message)
        }
        case ErrorCode.ORDER_LIMIT_ERROR: {
          throw new OrderLimitError(data.addItemToOrder.message, data.addItemToOrder.maxItems)
        }
        case ErrorCode.ORDER_MODIFICATION_ERROR: {
          throw new OrderModificationError(data.addItemToOrder.message)
        }
        default: {
          throw new Error(data.addItemToOrder.message)
        }
      }
    }

    if (isGraphqlType(data.addItemToOrder, 'Order')) {
      activeOrder.value = data.addItemToOrder
      return data.addItemToOrder
    }

    throw new Error('Failed to add item to order')
  }

  const removeOrderLine = async (line: OrderLineFragment) => {
    await transitionOrderToState('AddingItems')
    const { data } = await useGraphqlMutation('removeOrderLine', { orderLineId: line.id })

    if (isGraphqlError(data.removeOrderLine)) {
      activeOrderError.value = data.removeOrderLine
      switch (data.removeOrderLine.errorCode) {
        case ErrorCode.ORDER_MODIFICATION_ERROR: {
          throw new OrderModificationError(data.removeOrderLine.message)
        }
        // An OrderInterceptor refused the change (a gift-with-order line is
        // locked: `interceptorError: "gift-line-locked"`). Surface it exactly
        // like any other refused modification so callers keep one catch path.
        case ErrorCode.ORDER_INTERCEPTOR_ERROR: {
          throw new OrderModificationError(data.removeOrderLine.message)
        }
        default: {
          throw new Error(data.removeOrderLine.message)
        }
      }
    }

    if (isGraphqlType(data.removeOrderLine, 'Order')) {
      activeOrder.value = data.removeOrderLine
      return data.removeOrderLine
    }

    throw new Error('Failed to remove item from order')
  }

  const adjustOrderLine = async (line: OrderLineFragment, quantity: number) => {
    await transitionOrderToState('AddingItems')
    const { data } = await useGraphqlMutation('adjustOrderLine', { orderLineId: line.id, quantity })

    if (isGraphqlError(data.adjustOrderLine)) {
      activeOrderError.value = data.adjustOrderLine
      switch (data.adjustOrderLine.errorCode) {
        case ErrorCode.INSUFFICIENT_STOCK_ERROR: {
          throw new InsufficientStockError(data.adjustOrderLine.message, data.adjustOrderLine.quantityAvailable, data.adjustOrderLine.order)
        }
        case ErrorCode.NEGATIVE_QUANTITY_ERROR: {
          throw new NegativeQuantityError(data.adjustOrderLine.message)
        }
        case ErrorCode.ORDER_LIMIT_ERROR: {
          throw new OrderLimitError(data.adjustOrderLine.message, data.adjustOrderLine.maxItems)
        }
        case ErrorCode.ORDER_MODIFICATION_ERROR: {
          throw new OrderModificationError(data.adjustOrderLine.message)
        }
        // OrderInterceptor refusal (locked gift-with-order line) — same handling
        // as any other refused modification, see `removeOrderLine`.
        case ErrorCode.ORDER_INTERCEPTOR_ERROR: {
          throw new OrderModificationError(data.adjustOrderLine.message)
        }
        default: {
          throw new Error(data.adjustOrderLine.message)
        }
      }
    }

    if (isGraphqlType(data.adjustOrderLine, 'Order')) {
      activeOrder.value = data.adjustOrderLine
      return data.adjustOrderLine
    }

    throw new Error('Failed to adjust order line')
  }

  const addBundleToOrder = async (productBundleId: string, variants: ProductVariantFragment[], quantity = 1) => {
    await transitionOrderToState('AddingItems')

    if (!variants.length)
      throw new Error('No variants provided')

    const variantIds = variants.map(v => v.id)

    const { data } = await useGraphqlMutation('addBundleToOrder', { productBundleId, variantIds, quantity })

    if (isGraphqlError(data.addBundleToOrder)) {
      activeOrderError.value = data.addBundleToOrder
      throw new Error(data.addBundleToOrder.message)
    }

    if (isGraphqlType(data.addBundleToOrder, 'Order')) {
      activeOrder.value = data.addBundleToOrder
      return data.addBundleToOrder
    }

    throw new Error('Failed to add bundle to order')
  }

  // Customer: resolve personal-code ownership before even changing order state.
  const queryCache = useQueryCache()
  const prizeEmailConfirmation = usePrizeEmailConfirmationStore()
  const { prize: urlPrize, forgetPrize: forgetUrlPrize } = useUrlCoupon()
  const writeCustomerForOrder = async (customer: CreateCustomerInput) => {
    const previousCodes = activeOrder.value?.couponCodes.map((code: string) => code.trim().toUpperCase()) ?? []
    await transitionOrderToState('AddingItems')
    const { data } = await useGraphqlMutation('setCustomerForOrder', { input: customer })

    if (isGraphqlError(data.setCustomerForOrder)) {
      activeOrderError.value = data.setCustomerForOrder
      switch (data.setCustomerForOrder.errorCode) {
        case ErrorCode.ALREADY_LOGGED_IN_ERROR: {
          throw new AlreadyLoggedInError(data.setCustomerForOrder.message)
        }
        case ErrorCode.EMAIL_ADDRESS_CONFLICT_ERROR: {
          throw new EmailAddressConflictError(data.setCustomerForOrder.message)
        }
        case ErrorCode.GUEST_CHECKOUT_ERROR: {
          throw new GuestCheckoutError(data.setCustomerForOrder.message, data.setCustomerForOrder.errorDetail)
        }
        case ErrorCode.NO_ACTIVE_ORDER_ERROR: {
          throw new NoActiveOrderError(data.setCustomerForOrder.message)
        }
        default: {
          throw new Error(data.setCustomerForOrder.message)
        }
      }
    }

    if (isGraphqlType(data.setCustomerForOrder, 'Order')) {
      const rememberedCode = urlPrize.value?.code
      if (rememberedCode && previousCodes.includes(rememberedCode)
        && !data.setCustomerForOrder.couponCodes.some((code: string) => code.trim().toUpperCase() === rememberedCode)) {
        // Accepting reward removal must also stop the URL-code watcher from
        // immediately trying to reapply that same personal code.
        forgetUrlPrize()
      }
      // Checkout and cart use different caches; publish the reconciled lines to
      // both and prevent an older background fetch restoring the removed gift.
      queryCache.cancelQueries({ key: ['activeOrder'], exact: true })
      queryCache.setQueryData(['activeOrder'], data.setCustomerForOrder)
      activeOrder.value = data.setCustomerForOrder
      // A guest has a customer id from this point on. Identifying now, before
      // the shopper leaves for a payment redirect, ties the browsing so far to
      // the customer even if they never come back to the confirmation page.
      identifyPostHogCustomer(data.setCustomerForOrder.customer?.id)
      return data.setCustomerForOrder
    }

    throw new Error('Failed to set customer for order')
  }

  const customerEmailGuard = createPrizeEmailGuard({
    context: () => activeOrder.value,
    matches: async (emailAddress: string) => {
      const { data, errors } = await useGraphqlQuery('prizeCodeEmailMatches', { emailAddress })
      if (errors?.length || typeof data?.prizeCodeEmailMatches !== 'boolean')
        throw new Error('Unable to check prize code email')
      return data.prizeCodeEmailMatches
    },
    confirm: emailAddress => prizeEmailConfirmation.request(
      emailAddress,
      activeOrder.value?.giftProgress?.some((gift: OrderFragment['giftProgress'][number]) => gift.requiresCode && gift.rewardMode === 'CHEAPEST_IN_COLLECTION' && gift.granted > 0) ?? false,
    ),
    dismiss: () => prizeEmailConfirmation.finish(false),
    write: writeCustomerForOrder,
  })
  const setCustomerForOrder = (customer: CreateCustomerInput, options?: CustomerEmailSyncOptions) => customerEmailGuard.sync({ ...customer }, options)
  const customerEmailEdited = () => customerEmailGuard.emailEdited()

  // Shipping
  const setOrderShippingMethod = async (shippingMethod: Omit<ShippingMethodQuoteFragment, 'metadata' | 'price'>) => {
    await transitionOrderToState('AddingItems')
    const { data } = await useGraphqlMutation('setOrderShippingMethod', { shippingMethodId: shippingMethod.id })

    if (isGraphqlError(data.setOrderShippingMethod)) {
      switch (data.setOrderShippingMethod.errorCode) {
        case (ErrorCode.INELIGIBLE_SHIPPING_METHOD_ERROR): {
          throw new IneligibleShippingMethodError(data.setOrderShippingMethod.message)
        }
        case ErrorCode.NO_ACTIVE_ORDER_ERROR: {
          throw new NoActiveOrderError(data.setOrderShippingMethod.message)
        }
        case ErrorCode.ORDER_MODIFICATION_ERROR: {
          throw new OrderModificationError(data.setOrderShippingMethod.message)
        }
        default: {
          throw new Error(data.setOrderShippingMethod.message)
        }
      }
    }

    if (isGraphqlType(data.setOrderShippingMethod, 'Order')) {
      activeOrder.value = data.setOrderShippingMethod
      return
    }

    throw new Error('Failed to set shipping method for order')
  }

  const setOrderShippingAddress = async (address: Partial<CreateAddressInput>) => {
    await transitionOrderToState('AddingItems')

    const { data } = await useGraphqlMutation('setOrderShippingAddress', { input: assignBlankAddressFields(address) })

    if (isGraphqlError(data.setOrderShippingAddress)) {
      switch (data.setOrderShippingAddress.errorCode) {
        case ErrorCode.NO_ACTIVE_ORDER_ERROR: {
          throw new NoActiveOrderError(data.setOrderShippingAddress.message)
        }
        default: {
          throw new Error(data.setOrderShippingAddress.message)
        }
      }
    }

    if (isGraphqlType(data.setOrderShippingAddress, 'Order')) {
      activeOrder.value = data.setOrderShippingAddress
      return data.setOrderShippingAddress
    }

    throw new Error('Failed to set shipping address for order')
  }

  const setOrderBillingAddress = async (address: Partial<CreateAddressInput>) => {
    await transitionOrderToState('AddingItems')

    const { data } = await useGraphqlMutation('setOrderBillingAddress', {
      input: assignBlankAddressFields(address),
    })

    if (isGraphqlError(data.setOrderBillingAddress)) {
      switch (data.setOrderBillingAddress.errorCode) {
        case ErrorCode.NO_ACTIVE_ORDER_ERROR: {
          throw new NoActiveOrderError(data.setOrderBillingAddress.message)
        }
        default: {
          throw new Error(data.setOrderBillingAddress.message)
        }
      }
    }

    if (isGraphqlType(data.setOrderBillingAddress, 'Order')) {
      activeOrder.value = data.setOrderBillingAddress
      return data.setOrderBillingAddress
    }

    throw new Error('Failed to set billing address for order')
  }

  const applyCouponCode = async (couponCode: string) => {
    await transitionOrderToState('AddingItems')
    const normalizedCode = couponCode.toUpperCase()
    const { data } = await useGraphqlMutation('applyCouponCode', { couponCode: normalizedCode })

    const result = data.applyCouponCode
    if (isGraphQLError(result)) {
      // TypeScript cannot discriminate this union on `errorCode` — every
      // fragment types it as the full ErrorCode union — so the per-type fields
      // are read structurally. The layer document already selects them.
      const code = 'couponCode' in result && typeof result.couponCode === 'string' && result.couponCode
        ? result.couponCode
        : normalizedCode
      const limit = 'limit' in result && typeof result.limit === 'number' ? result.limit : undefined

      switch (result.errorCode) {
        case ErrorCode.COUPON_CODE_EXPIRED_ERROR: {
          throw new CouponCodeExpiredError(result.message, code)
        }
        case ErrorCode.COUPON_CODE_INVALID_ERROR: {
          throw new CouponCodeInvalidError(result.message, code)
        }
        case ErrorCode.COUPON_CODE_LIMIT_ERROR: {
          throw new CouponCodeLimitError(result.message, code, limit)
        }
        case ErrorCode.COUPON_CODE_COMBINATION_ERROR: {
          throw new CouponCodeCombinationError(result.message, code)
        }
        default: {
          throw new Error(result.message)
        }
      }
    }

    if (isGraphqlType(result, 'Order')) {
      activeOrder.value = result
      return
    }

    throw new Error('Failed to apply coupon code to order')
  }

  const removeCouponCode = async (couponCode: string) => {
    await transitionOrderToState('AddingItems')
    const { data } = await useGraphqlMutation('removeCouponCode', { couponCode })

    if (isGraphQLError(data.removeCouponCode)) {
      throw new Error(data.removeCouponCode.message)
    }

    if (isGraphqlType(data.removeCouponCode, 'Order')) {
      activeOrder.value = data.removeCouponCode
      return
    }

    throw new Error('Failed to remove coupon code from order')
  }

  const addPaymentToOrder = async (input: PaymentInput) => {
    await transitionOrderToState('ArrangingPayment')
    const { data } = await useGraphqlMutation('addPaymentToOrder', { input })

    if (isGraphqlError(data.addPaymentToOrder)) {
      activeOrderError.value = data.addPaymentToOrder
      switch (data.addPaymentToOrder.errorCode) {
        case ErrorCode.INELIGIBLE_PAYMENT_METHOD_ERROR: {
          throw new IneligiblePaymentMethodError(data.addPaymentToOrder.message, data.addPaymentToOrder.eligibilityCheckerMessage)
        }
        case ErrorCode.NO_ACTIVE_ORDER_ERROR: {
          throw new NoActiveOrderError(data.addPaymentToOrder.message)
        }
        case ErrorCode.ORDER_PAYMENT_STATE_ERROR: {
          throw new OrderPaymentStateError(data.addPaymentToOrder.message)
        }
        case ErrorCode.ORDER_STATE_TRANSITION_ERROR: {
          throw new OrderStateTransitionError(data.addPaymentToOrder.message, data.addPaymentToOrder.fromState, data.addPaymentToOrder.toState, data.addPaymentToOrder.transitionError)
        }
        case ErrorCode.PAYMENT_DECLINED_ERROR: {
          throw new PaymentDeclinedError(data.addPaymentToOrder.message, data.addPaymentToOrder.paymentErrorMessage)
        }
        case ErrorCode.PAYMENT_FAILED_ERROR: {
          throw new PaymentFailedError(data.addPaymentToOrder.message, data.addPaymentToOrder.paymentErrorMessage)
        }
        case ErrorCode.COUPON_REMOVED_DURING_CHECKOUT_ERROR: {
          const removed = 'removedCouponCodes' in data.addPaymentToOrder && Array.isArray(data.addPaymentToOrder.removedCouponCodes)
            ? data.addPaymentToOrder.removedCouponCodes
            : []
          throw new CouponRemovedDuringCheckoutError(data.addPaymentToOrder.message, removed)
        }
        default: {
          throw new Error(data.addPaymentToOrder.message)
        }
      }
    }

    if (isGraphqlType(data.addPaymentToOrder, 'Order')) {
      // activeOrder.value = result
      return data.addPaymentToOrder
    }

    throw new Error('Failed to add payment to order')
  }

  const fetchEligiblePaymentMethods = async () => {
    const { data } = await useGraphqlQuery('eligiblePaymentMethods')
    const eligiblePaymentMethods = data.eligiblePaymentMethods as PaymentMethodQuoteFragment[]

    return eligiblePaymentMethods.filter(method => method.isEligible)?.map((method) => {
      let icon = 'i-heroicons:credit-card-solid'
      let logo, external

      if (method.code?.includes('bank'))
        icon = 'i-heroicons:building-library-solid'

      if (method.code?.includes('comgate')) {
        logo = 'payment:blik'
        external = true
      }

      const props = method.code?.includes('stripe') ? { stripe: 'card' } : {}

      return { ...method, icon, logo, external, ...props } as PaymentMethod
    })
  }

  const setOrderCustomFields = async (input: UpdateOrderInput) => {
    if (!input?.customFields)
      return

    const customFields = Object.entries(input.customFields).reduce((result, [key, value]) => {
      if (result) {
        result[key as keyof UpdateOrderCustomFieldsInput] = typeof value === 'object' && value !== null ? JSON.stringify(value) : value
      }
      return result
    }, {} as IUpdateOrderInput['customFields'])

    const { data } = await useGraphqlMutation('setOrderCustomFields', { input: { customFields } })

    if (isGraphqlError(data.setOrderCustomFields)) {
      activeOrderError.value = data.setOrderCustomFields
      switch (data.setOrderCustomFields.errorCode) {
        case ErrorCode.NO_ACTIVE_ORDER_ERROR: {
          throw new NoActiveOrderError(data.setOrderCustomFields.message)
        }
        default: {
          throw new Error(data.setOrderCustomFields.message)
        }
      }
    }

    if (isGraphqlType(data.setOrderCustomFields, 'Order')) {
      activeOrder.value = data.setOrderCustomFields
      return data.setOrderCustomFields
    }

    throw new Error('Failed to set custom fields for order')
  }

  // Records the intermediate payment method AND recalculates order surcharges
  // (e.g. the cash-on-delivery fee). Plain setOrderCustomFields only writes the
  // field — only this mutation runs applySurchargesToOrder on the backend, so it
  // is what actually adds/removes the COD fee when the payment method changes.
  const setIntermediatePaymentMethod = async (paymentMethodCode: string) => {
    const { data } = await useGraphqlMutation('setIntermediatePaymentMethod', { paymentMethodCode })

    if (isGraphqlError(data.setIntermediatePaymentMethod)) {
      activeOrderError.value = data.setIntermediatePaymentMethod
      throw new Error(data.setIntermediatePaymentMethod.message)
    }

    if (isGraphqlType(data.setIntermediatePaymentMethod, 'Order')) {
      activeOrder.value = data.setIntermediatePaymentMethod
      return data.setIntermediatePaymentMethod
    }
  }

  const orderTotal = computed(() => {
    return activeOrder.value?.total || 0
  })

  const orderTotalQuantity = computed(() => activeOrder.value?.totalQuantity || 0)

  const parcelPoint = computed(() => {
    if (activeOrder.value?.customFields?.parcelPoint)
      return destr<ParcelPoint>(activeOrder.value.customFields.parcelPoint)
    return null
  })

  const postNumber = computed(() => activeOrder.value?.customFields?.postNumber ?? '')

  return {
    provide: {
      activeOrder: {
        hasActiveOrder,
        cartDrawerOpen,
        activeOrder,
        transitionOrderToState,
        refreshActiveOrder,
        addItemToOrder,
        addBundleToOrder,
        removeOrderLine,
        adjustOrderLine,
        setCustomerForOrder,
        customerEmailEdited,
        setOrderShippingMethod,
        setOrderShippingAddress,
        setOrderBillingAddress,
        addPaymentToOrder,
        applyCouponCode,
        removeCouponCode,
        fetchEligiblePaymentMethods,
        setOrderCustomFields,
        setIntermediatePaymentMethod,
        orderTotal,
        orderTotalQuantity,
        parcelPoint,
        postNumber,
      },
    },
  }
})

interface ActiveOrderPlugin {
  hasHasActiveOrder: ComputedRef<boolean>
  cartDrawerOpen: Ref<boolean>
  activeOrder: Ref<OrderFragment>
  transitionOrderToState: (nextState: OrderState) => Promise<void>
  refreshActiveOrder: (opts?: AsyncDataExecuteOptions) => Promise<void>
  addItemToOrder: (productVariant: ProductVariantFragment, quantity?: number) => Promise<void>
  addBundleToOrder: (productBundleId: string, variants: ProductVariantFragment[], quantity?: number) => Promise<void>
  removeOrderLine: (line: OrderLineFragment) => Promise<void>
  adjustOrderLine: (line: OrderLineFragment, quantity: number) => Promise<void>
  setCustomerForOrder: (customer: CreateCustomerInput, options?: CustomerEmailSyncOptions) => Promise<OrderFragment>
  customerEmailEdited: () => void
  setOrderShippingMethod: (shippingMethod: Omit<ShippingMethodQuoteFragment, 'metadata' | 'price'>) => Promise<void>
  setOrderShippingAddress: (address: Partial<CreateAddressInput>) => Promise<void>
  setOrderBillingAddress: (address: Partial<CreateAddressInput>) => Promise<void>
  addPaymentToOrder: (input: PaymentInput) => Promise<void>
  applyCouponCode: (couponCode: string) => Promise<void>
  removeCouponCode: (couponCode: string) => Promise<void>
  fetchEligiblePaymentMethods: () => Promise<PaymentMethod[]>
  setOrderCustomFields: (input: UpdateOrderInput) => Promise<void>
  setIntermediatePaymentMethod: (paymentMethodCode: string) => Promise<OrderFragment | undefined>
  orderTotal: ComputedRef<number>
  orderTotalQuantity: ComputedRef<number>
  parcelPoint: ComputedRef<ParcelPoint | null>
  postNumber: ComputedRef<string>
}

declare module '#app' {
  interface NuxtApp {
    $activeOrder: ActiveOrderPlugin
  }
  interface PageMeta {
    checkout?: boolean
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $activeOrder: ActiveOrderPlugin
  }
}
